// =====================================================
// FTD Cyber Portal - Add Company Modal Component
// =====================================================
// This component handles the Add Company form with industry dropdown
// Add this to your main dashboard component

import React, { useState } from 'react';

import { X, Building2, AlertCircle } from 'lucide-react';
// =====================================================
// INDUSTRY OPTIONS
// =====================================================
const INDUSTRIES = [
  'Technology',
  'Financial Services',
  'Healthcare',
  'Manufacturing',
  'Retail',
  'Education',
  'Government',
  'Energy',
  'Telecommunications',
  'Transportation',
  'Real Estate',
  'Hospitality',
  'Media & Entertainment',
  'Professional Services',
  'Insurance',
  'Pharmaceuticals',
  'Aerospace & Defense',
  'Automotive',
  'Agriculture',
  'Construction',
  'Non-Profit',
  'Legal Services',
  'Consulting',
  'Other'
];

// =====================================================
// SUBSCRIPTION TIER OPTIONS
// =====================================================
const SUBSCRIPTION_TIERS = [
  {
    id: 'starter',
    name: 'Starter',
    maxUsers: 10,
    maxIncidents: 1000,
    description: 'For small teams'
  },
  {
    id: 'professional',
    name: 'Professional',
    maxUsers: 20,
    maxIncidents: 5000,
    description: 'For growing companies'
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    maxUsers: 100,
    maxIncidents: 50000,
    description: 'For large organizations'
  }
];

// =====================================================
// ADD COMPANY MODAL COMPONENT
// =====================================================
const AddCompanyModal = ({ isOpen, onClose, onCompanyAdded }) => {
   
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    industry: '',
    subscriptionTier: 'professional',
    maxUsers: 20,
    maxIncidents: 5000,
    primaryContactName: '',
    primaryContactEmail: ''
  });

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // =====================================================
  // HELPER FUNCTIONS
  // =====================================================
  
  // Generate slug from company name
  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-')          // Replace spaces with hyphens
      .replace(/-+/g, '-')           // Replace multiple hyphens with single
      .replace(/^-|-$/g, '');        // Remove leading/trailing hyphens
  };

  // Handle company name change
  const handleNameChange = (value) => {
    setFormData({
      ...formData,
      name: value,
      slug: generateSlug(value)
    });
  };

  // Handle subscription tier change
  const handleTierChange = (tierId) => {
    const tier = SUBSCRIPTION_TIERS.find(t => t.id === tierId);
    if (tier) {
      setFormData({
        ...formData,
        subscriptionTier: tierId,
        maxUsers: tier.maxUsers,
        maxIncidents: tier.maxIncidents
      });
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      industry: '',
      subscriptionTier: 'professional',
      maxUsers: 20,
      maxIncidents: 5000,
      primaryContactName: '',
      primaryContactEmail: ''
    });
    setError('');
  };

  // =====================================================
  // FORM SUBMISSION
  // =====================================================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Get Clerk token
      const token = await window.Clerk?.session?.getToken();
      
      if (!token) {
        throw new Error('Authentication required. Please log in again.');
      }

      // Validate required fields
      if (!formData.name || !formData.slug) {
        throw new Error('Company name and slug are required');
      }

      // Validate email if provided
      if (formData.primaryContactEmail) {
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailPattern.test(formData.primaryContactEmail)) {
          throw new Error('Invalid email format');
        }
      }

      // Make API call
      const response = await fetch('/api/companies', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: formData.name,
          slug: formData.slug,
          industry: formData.industry || null,
          subscriptionTier: formData.subscriptionTier,
          maxUsers: parseInt(formData.maxUsers),
          maxIncidents: parseInt(formData.maxIncidents),
          primaryContactName: formData.primaryContactName || null,
          primaryContactEmail: formData.primaryContactEmail || null
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create company');
      }

      // Success!
      console.log('Company created:', data.company);
      
      // Call callback if provided
      if (onCompanyAdded) {
        onCompanyAdded(data.company);
      }

      // Reset and close
      resetForm();
      onClose();
      
      // Show success message (you can replace with your toast/notification system)
      alert(`Company "${data.company.name}" created successfully!`);

    } catch (err) {
      console.error('Error creating company:', err);
      setError(err.message || 'Failed to create company');
    } finally {
      setLoading(false);
    }
  };

  // Don't render if not open
  if (!isOpen) return null;

  // =====================================================
  // RENDER
  // =====================================================
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-purple-500/30 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-800/50 bg-gradient-to-r from-purple-900/30 to-slate-900/30">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-slate-100 flex items-center gap-2">
                <Building2 className="w-5 h-5 text-purple-400" />
                Add New Company
              </h2>
              <p className="text-sm text-slate-400 mt-1">
                Create a new organization in the system
              </p>
            </div>
            <button
              onClick={() => {
                resetForm();
                onClose();
              }}
              className="p-2 hover:bg-slate-800/50 rounded-lg transition-all group"
              disabled={loading}
            >
              <X className="w-5 h-5 text-slate-400 group-hover:text-slate-200" />
            </button>
          </div>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          
          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-red-400">Error</p>
                <p className="text-sm text-red-300 mt-1">{error}</p>
              </div>
            </div>
          )}

          <div className="space-y-6">
            
            {/* ========== COMPANY INFORMATION ========== */}
            <div>
              <h3 className="text-sm font-semibold text-slate-300 mb-4">
                Company Information
              </h3>
              
              <div className="space-y-4">
                
                {/* Company Name */}
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-2">
                    Company Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                    placeholder="e.g., Acme Corporation"
                    required
                    disabled={loading}
                  />
                </div>

                {/* Company Slug */}
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-2">
                    Company Slug <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all font-mono text-sm"
                    placeholder="e.g., acme-corp"
                    pattern="[a-z0-9-]+"
                    required
                    disabled={loading}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Auto-generated from name. Use lowercase letters, numbers, and hyphens only.
                  </p>
                </div>

                {/* Industry Dropdown */}
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-2">
                    Industry
                  </label>
                  <select
                    value={formData.industry}
                    onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                    className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                    disabled={loading}
                  >
                    <option value="">Select an industry (optional)</option>
                    {INDUSTRIES.map((industry) => (
                      <option key={industry} value={industry}>
                        {industry}
                      </option>
                    ))}
                  </select>
                </div>

              </div>
            </div>

            {/* ========== SUBSCRIPTION SETTINGS ========== */}
            <div>
              <h3 className="text-sm font-semibold text-slate-300 mb-4">
                Subscription Settings
              </h3>
              
              <div className="space-y-4">
                
                {/* Subscription Tier */}
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-2">
                    Service Plan
                  </label>
                  <div className="grid grid-cols-3 gap-3">
                    {SUBSCRIPTION_TIERS.map((tier) => (
                      <button
                        key={tier.id}
                        type="button"
                        onClick={() => handleTierChange(tier.id)}
                        disabled={loading}
                        className={`p-3 rounded-lg border-2 transition-all text-left ${
                          formData.subscriptionTier === tier.id
                            ? 'border-purple-500 bg-purple-500/10'
                            : 'border-slate-700/50 bg-slate-800/30 hover:border-slate-600'
                        }`}
                      >
                        <p className="text-sm font-semibold text-slate-200">{tier.name}</p>
                        <p className="text-xs text-slate-400 mt-1">{tier.description}</p>
                        <p className="text-xs text-slate-500 mt-2">
                          {tier.maxUsers} users • {tier.maxIncidents.toLocaleString()} incidents
                        </p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Max Users & Incidents */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Maximum Users
                    </label>
                    <input
                      type="number"
                      value={formData.maxUsers}
                      onChange={(e) => setFormData({ ...formData, maxUsers: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      min="1"
                      disabled={loading}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-400 mb-2">
                      Maximum Incidents
                    </label>
                    <input
                      type="number"
                      value={formData.maxIncidents}
                      onChange={(e) => setFormData({ ...formData, maxIncidents: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                      min="1"
                      disabled={loading}
                    />
                  </div>
                </div>

              </div>
            </div>

            {/* ========== PRIMARY CONTACT ========== */}
            <div>
              <h3 className="text-sm font-semibold text-slate-300 mb-4">
                Primary Contact (Optional)
              </h3>
              
              <div className="space-y-4">
                
                {/* Contact Name */}
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-2">
                    Contact Name
                  </label>
                  <input
                    type="text"
                    value={formData.primaryContactName}
                    onChange={(e) => setFormData({ ...formData, primaryContactName: e.target.value })}
                    className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                    placeholder="e.g., John Doe"
                    disabled={loading}
                  />
                </div>

                {/* Contact Email */}
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-2">
                    Contact Email
                  </label>
                  <input
                    type="email"
                    value={formData.primaryContactEmail}
                    onChange={(e) => setFormData({ ...formData, primaryContactEmail: e.target.value })}
                    className="w-full px-4 py-2.5 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-200 placeholder-slate-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
                    placeholder="e.g., john.doe@acmecorp.com"
                    disabled={loading}
                  />
                </div>

              </div>
            </div>

          </div>
        </form>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-800/50 bg-slate-900/50 flex justify-end gap-3">
          <button
            type="button"
            onClick={() => {
              resetForm();
              onClose();
            }}
            disabled={loading}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-sm font-medium text-slate-200 transition-all"
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={loading || !formData.name || !formData.slug}
            className="px-4 py-2 bg-purple-500 hover:bg-purple-600 disabled:bg-slate-700 disabled:text-slate-500 disabled:cursor-not-allowed rounded-lg text-sm font-medium text-white transition-all flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Creating...
              </>
            ) : (
              'Create Company'
            )}
          </button>
        </div>

      </div>
    </div>
  );
};

export default AddCompanyModal;

// =====================================================
// USAGE EXAMPLE
// =====================================================
/*
// In your main dashboard component:

import AddCompanyModal from './AddCompanyModal';

function Dashboard() {
  const [showAddCompanyModal, setShowAddCompanyModal] = useState(false);

  const handleCompanyAdded = (newCompany) => {
    console.log('New company added:', newCompany);
    // Refresh your companies list here
    fetchCompanies();
  };

  return (
    <div>
      <button onClick={() => setShowAddCompanyModal(true)}>
        Add Company
      </button>

      <AddCompanyModal
        isOpen={showAddCompanyModal}
        onClose={() => setShowAddCompanyModal(false)}
        onCompanyAdded={handleCompanyAdded}
      />
    </div>
  );
}
*/
